import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react'
import { Row, Col, Card, InputNumber, Input, Button, Collapse, Space, message, Typography, Timeline, Select, Tag, Skeleton, Radio, Empty } from 'antd'
import { Link } from 'react-router-dom'
import { ArrowLeftOutlined } from '@ant-design/icons'
import { v4 as uuid } from 'uuid'
import qs from 'qs'
import moment from 'moment'
import { get, isEmpty, debounce, set, cloneDeep, sortBy } from 'lodash'
import {
  GetQualityCheckResultAPI,
  QualityCheckResultParamsType,
  AddQualityCheckResultAPI,
  GetQualityCheckPlanListAPI,
} from '@services/quality-inspection'
import {
  QualityCheckResultResType,
  QualityCheckPlanItemType,
  GetQualityCheckPlanListParamsType,
  AddQualityCheckResultParamsType,
  CALL_RECORD_TYPE_ENUM,
} from '@types'
import { getStaffName } from '@library'
import { getRecordPosition, getNextRecordId, getPrevRecordId } from '@library/queue'
import { getUserAuthPoint } from '@services/session'
import AUTH_POINT_MAP from '@configs/auth-point'
import { TableIdEnum } from '@configs/table'
import store, { globalStore } from '@store'
import { useCallRecordDetail } from '../call-record/useCallRecordDetail'
import KeyWordTag, { KeyWotdItemType } from './components/KeyWordTab'
import {
  KeyWordTypeEnum,
  sliderLeftWidth,
  sliderRightWidth,
  tabWidth,
  keyWordTypeOptions,
  PAGE_TYPE_MAP,
  PAGE_TYPE_ENUM,
} from './constants/index'
import './style.less'

const { menuTaskCallScoreU } = AUTH_POINT_MAP

const ScoreDetailPage: React.FC = () => {
  const authPointMap = globalStore(s => s.authPointMap)

  const {
    recordId,
    pageType = PAGE_TYPE_ENUM.T3C,
    queue: queueIdStr,
  } = qs.parse(location.search, { ignoreQueryPrefix: true })

  // 队列模式相关
  const queueId = queueIdStr ? Number(queueIdStr) : null
  const isQueueMode = !!queueId

  const {
    callRecordDetail,
    setCallRecordDetail,
    getCallRecordDetail,
    open,
    setOpen,
  } = useCallRecordDetail(PAGE_TYPE_MAP[pageType as PAGE_TYPE_ENUM] as TableIdEnum.TCCC_CALL_RECORD | TableIdEnum.MEETING_CALL_RECORD)

  const [authPoint, setAuthPoint] = useState(null)
  const audioRef = useRef<HTMLAudioElement>(null)
  // 质检方案详情
  const [qualityCheckRes, setQualityCheckRes] = useState<QualityCheckResultResType>()
  // 评语
  const [remark, setRemark] = useState('')
  const [previousBtn, setPreviousBtn] = useState(true)
  const [nextBtn, setNextBtn] = useState(true)
  const [loading, setLoading] = useState(false)
  const [audioCurrentTime, setAudioCurrentTime] = useState(0)
  // 选择的质检方案
  const [selectedPlan, setSelectedPlan] = useState<QualityCheckPlanItemType>()
  const [qualityCheckPlanStatus, setQualityCheckPlanStatus] = useState<'' | 'error' | 'warning'>('')
  // 质检方案列表
  const [qualityCheckList, setQualityCheckList] = useState([])
  const textListRef = useRef(null)
  const [selectKeyWord, setSelectKeyWord] = useState(null)
  const [keyWordType, setKeyWordType] = useState<KeyWordTypeEnum>(KeyWordTypeEnum.Group) // 关键词展示形式
  const [textListHeight, setTextListHeight] = useState('400px')

  // 队列模式下的位置信息
  const [queuePosition, setQueuePosition] = useState<{
    current: number
    total: number
    hasPrev: boolean
    hasNext: boolean
  } | null>(null)

  // 处理关键词
  const getKeyWordTag = useCallback(() => {
    if (isEmpty(callRecordDetail) || !audioRef.current) return []
    const isT3C = PAGE_TYPE_MAP[pageType as PAGE_TYPE_ENUM] === TableIdEnum.TCCC_CALL_RECORD
    const { customerKeywordList = [], salesKeywordList = [], callEventList = [], audioMaxS } = callRecordDetail || {}
    let list = [...customerKeywordList, ...salesKeywordList]
    // if (!list.length || !callEventList.length) return []
    if (!list.length) return []
    list = sortBy(list, 'AppearanceTimestamp')
    // T3C属于一期，后台对关键词的时间做了处理，将原本的 00:01:12 转为为了年月日时分秒，对于前端来说搞复杂了，所以需要转换
    // 获取音频的最大秒数
    if (callEventList.length && isT3C) {
      const timestamp = get(callEventList, '1.timestamp', '0') as string
      let startTime: string | number = Number(timestamp) * 1000
      startTime = moment(Number(startTime)).format('YYYY-MM-DD HH:mm:ss')
      list = list.map(item => {
        // @ts-ignore
        const endTime = moment(Number(item.AppearanceTimestamp))
        const duration = moment.duration(endTime.diff(startTime))
        const timeInSeconds = Number(duration.hours()) * 3600 + Number(duration.minutes()) * 60 + Number(duration.seconds())
        const left = Number((timeInSeconds / audioMaxS).toFixed(2))
        const tagLeft = (audioRef.current.offsetWidth - (sliderLeftWidth + sliderRightWidth)) * left
        // @ts-ignore
        item.diffTime = [duration.hours(), duration.minutes(), duration.seconds()].map(item => `${item}`.padStart(2, '0')).join(':')
        // @ts-ignore
        item.left = `calc(${sliderLeftWidth}px + ${tagLeft}px - ${tabWidth / 2}px)`
        // @ts-ignore
        item.standardTime = endTime.format('YYYY-MM-DD HH:mm:ss')
        return item
      })
    }
    // 腾会通话记录
    if (!isT3C) {
      list = list.map((item: any) => {
        // 当前关键词的秒数
        const currentKeyWordSeconds = moment.duration(item.meetingAppearanceTime).asSeconds()
        const left = Number((currentKeyWordSeconds / audioMaxS).toFixed(2))
        const tagLeft = (audioRef.current.offsetWidth - (sliderLeftWidth + sliderRightWidth)) * left
        item.left = `calc(${sliderLeftWidth}px + ${tagLeft}px - ${tabWidth / 2}px)`
        return item
      })
    }
    return list
  }, [callRecordDetail, audioRef, pageType])
  const keyWordTag = useMemo(() => getKeyWordTag(), [getKeyWordTag])

  // 当前总分
  const currentTotalScore = useMemo(() => {
    if (isEmpty(qualityCheckRes)) return '-'
    const total = qualityCheckRes?.dimensionsScore?.reduce((pre, cur) => pre + (cur?.itemsTotalScore ?? 0), 0)
    return total ?? 0
  }, [qualityCheckRes])

  useEffect(() => {
    const audioElement = audioRef.current
    // 滚轮事件防抖
    const debouncedWheelHandler = debounce(async () => {
      if (audioElement) {
        audioElement.pause()
        // 添加延迟确保状态稳定
        await new Promise(resolve => setTimeout(resolve, 100))
      }
    }, 100)
    if (textListRef?.current) {
      // 监听滚动事件，滚动时暂停音频播放
      textListRef.current.addEventListener('wheel', debouncedWheelHandler)
      const { top = 400 } = textListRef.current.getBoundingClientRect() || {}
      setTextListHeight(`calc(100vh - ${top}px - 60px - 72px - 46px)`)
    }
    if (!audioElement) return
    const handleLoadedMetadata = () => {
      if (audioElement.duration && !isNaN(audioElement.duration)) {
        setCallRecordDetail(v => ({ ...v, audioMaxS: Math.round(audioElement.duration) }))
      }
    }
    // 音频播放中
    const handleTimeUpdate = (e) => {
      const currentTime = parseInt(e?.target?.currentTime)
      setAudioCurrentTime(currentTime)
      const activeEl = textListRef?.current?.querySelector('.active')
      if (textListRef?.current && activeEl) {
        textListRef.current.scrollTo({ top: activeEl.offsetTop, behavior: 'smooth' })
      }
    }
    // 音频播放完
    const handleEnded = () => {
      setAudioCurrentTime(0)
      setSelectKeyWord(null)
      if (textListRef?.current) {
        textListRef.current.scrollTo({ top: 0, behavior: 'smooth' })
      }
    }
    audioElement.addEventListener('loadedmetadata', handleLoadedMetadata)
    audioElement.addEventListener('timeupdate', handleTimeUpdate)
    audioElement.addEventListener('ended', handleEnded)

    return () => {
      audioElement.removeEventListener('loadedmetadata', handleLoadedMetadata)
      audioElement.removeEventListener('timeupdate', handleTimeUpdate)
      audioElement.removeEventListener('ended', handleEnded)
      textListRef?.current && textListRef.current.removeEventListener('wheel', debouncedWheelHandler)
    }
  }, [textListRef, audioRef, callRecordDetail])

  /**
   * 获取权限点
   */
  const getAuthPoint = async () => {
    const [res, err] = await getUserAuthPoint([
      { object: 'tcc.menu.task.call.score', action: 'r' },
      { object: 'tcc.menu.task.call.score', action: 'u' },
    ])
    if (err) {
      setAuthPoint({})
      return
    }
    const pointList = get(res, 'authPoints', [])
    const obj = {}
    pointList.forEach(item => {
      const key = `${item.object}-${item.action}`
      obj[key] = item.result
    })
    // const obj1 = {
    //   "tcc.menu.task.call.score-r": true,
    //   "tcc.menu.task.call.score-u": false
    // }
    setAuthPoint(obj)
  }

  useEffect(() => {
    getAuthPoint()
  }, [])

  /**
   * 队列模式：获取当前记录在队列中的位置
   */
  const fetchQueuePosition = async () => {
    if (!isQueueMode || !queueId || !recordId) return
    const position = await getRecordPosition(queueId, Number(recordId))
    if (position) {
      setQueuePosition(position)
      setPreviousBtn(!position.hasPrev)
      setNextBtn(!position.hasNext)
    }
  }

  useEffect(() => {
    if (isQueueMode) {
      fetchQueuePosition()
    }
  }, [isQueueMode, queueId, recordId])

  /**
   * 处理时间轴点击事件 / 关键词Tag点击事件
   * @param time 时间
   */
  const handleTimelineOnClick = async (time: string) => {
    if (!audioRef.current || !callRecordDetail?.recordUrl) return
    try {
      // 将时间字符串转换为秒数
      const [h, m, s] = time.split(':').map(Number)
      const timeInSeconds = (h * 3600) + (m * 60) + s
      // 先暂停并重置当前时间
      audioRef.current.pause()
      audioRef.current.currentTime = timeInSeconds
      // 添加延迟确保状态稳定
      await new Promise(resolve => setTimeout(resolve, 100))
      // 尝试播放并捕获可能的错误
      const playPromise = audioRef.current.play()
      if (playPromise !== undefined) {
        playPromise.catch(err => {
          console.warn('音频播放失败:', err)
        })
      }
    } catch (error) {
      console.warn(`音频播放失败：${error}`)
    }
  }

  /**
   * 获取质检结果
   */
  const getQualityCheckResult = async (recordId) => {
    const params: QualityCheckResultParamsType = {
      callRecordId: Number(recordId),
      callRecordType: pageType === PAGE_TYPE_ENUM.T3C ? CALL_RECORD_TYPE_ENUM.T3C : CALL_RECORD_TYPE_ENUM.MEETING,
    }
    const [res, err] = await GetQualityCheckResultAPI(params)
    if (err) {
      message.error(err.message)
      return
    }
    const result: QualityCheckResultResType = get(res, 'result', {} as QualityCheckResultResType)
    const dimensionsScore = get(result, 'dimensionsScore', [])
    setRemark(result.remark || '')
    dimensionsScore.forEach(item => {
      item.id = uuid()
      item.itemsTotalScoreFromAPI = item.items.reduce((pre, cur) => (pre + (cur.score ?? 0)), 0)
      item.itemsTotalScore = 0
      item.items.forEach(i => {
        i.subId = uuid()
        i.subScore = i.score
      })
    })
    getQualityCheckPlanList(1, 1, { id: result.qualityCheckPlanId }, result)
  }

  /**
   * 获取质检方案列表
   */
  const getQualityCheckPlanList = async (page: number = 1, pageSize: number = 5, options?: { name?: string, id?: number }, result?) => {
    const params: GetQualityCheckPlanListParamsType = { page, pageSize }
    Object.assign(params, options)
    const { id } = options || {}
    const [res, err] = await GetQualityCheckPlanListAPI(params)
    if (err) {
      message.error(err.message)
      return
    }
    const list = get(res, 'list', [])
    if (id) {
      const _result = get(list, '0', {})
      result = result || {}
      result.id = id
      result.name = _result.name
      result.qualifiedScore = _result.qualifiedScore
      if (result.dimensionsScore) {
        result.dimensionsScore.forEach((item, index) => {
          const _items = get(_result, `dimensions.${index}.items`, [])
          item.itemsTotalScoreFromAPI = _items.reduce((pre, cur) => (pre + (cur.score ?? 0)), 0)
          item.itemsTotalScore = item.items.reduce((pre, cur) => (pre + (cur.score ?? 0)), 0)
          item.items.forEach((subItem, subIndex) => {
            subItem.subScore = get(_result, `dimensions.${index}.items.${subIndex}.score`, 0)
          })
        })
      } else {
        const dimensionsScore = cloneDeep(_result.dimensions)
        dimensionsScore.forEach(item => {
          item.id = uuid()
          item.itemsTotalScoreFromAPI = item.items.reduce((pre, cur) => (pre + (cur.score ?? 0)), 0)
          item.itemsTotalScore = 0
          item.items.forEach(i => {
            i.subId = uuid()
            i.subScore = i.score
            i.score = 0
          })
        })
        result.dimensionsScore = dimensionsScore
      }
      setQualityCheckRes(result)
      setSelectedPlan(result)
      return
    }
    setQualityCheckList(list)
  }

  useEffect(() => {
    const fetchData = async () => {
      if (recordId && pageType) {
        setOpen(false)
        const detail = await getCallRecordDetail(recordId, {}, true)
        // 已质检的记录需要获取质检结果
        if (detail?.qualityCheckStatus === 1) {
          getQualityCheckResult(recordId)
        }
      }
      getQualityCheckPlanList(1, 20)
    }
    fetchData()
  }, [recordId, pageType])

  /**
   * 队列模式：处理上一条或下一条
   * @param type 处理类型 - previous 上一条，next 下一条
   */
  const handlePreviousOrNext = debounce(async (type: 'previous' | 'next') => {
    if (!queueId || !recordId) return

    message.destroy()
    setLoading(true)

    // 获取上/下一条记录ID
    const targetRecordId = type === 'previous'
      ? await getPrevRecordId(queueId, Number(recordId))
      : await getNextRecordId(queueId, Number(recordId))

    if (!targetRecordId) {
      setLoading(false)
      message.info(type === 'previous' ? '已经是第一条了' : '已经是最后一条了，评分完毕！')
      return
    }

    // 重置状态
    setRemark('')
    setQualityCheckRes(null)
    setSelectedPlan(null)
    setOpen(false)

    // 更新 URL 参数
    const currentUrl = new URL(window.location.href)
    const searchParams = new URLSearchParams(currentUrl.search)
    searchParams.set('recordId', String(targetRecordId))
    currentUrl.search = searchParams.toString()
    window.history.replaceState({}, '', currentUrl.toString())

    // 获取新记录的详情
    const detail = await getCallRecordDetail(targetRecordId, {}, true)

    // 检查目标记录是否已评分，如果已评分则获取评分结果
    // qualityCheckStatus === 1 表示已评分
    if (detail?.qualityCheckStatus === 1) {
      await getQualityCheckResult(targetRecordId)
    }

    // 更新队列位置信息
    const position = await getRecordPosition(queueId, targetRecordId)
    if (position) {
      setQueuePosition(position)
      setPreviousBtn(!position.hasPrev)
      setNextBtn(!position.hasNext)
    }

    setLoading(false)
    getQualityCheckPlanList(1, 20)
  }, 500)

  /**
   * 评分项修改分值
   * @param path 修改分值对应在qualityCheckRes的路径
   * @param val
   */
  const handleScore = (path: string, val: number) => {
    setQualityCheckRes(v => {
      set(v, path, val)
      v.dimensionsScore.forEach(item => {
        item.itemsTotalScore = item.items.reduce((pre, cur) => (pre + (cur.score ?? 0)), 0)
      })
      return { ...v }
    })
  }

  /**
   * 搜索质检方案
   * @param val
   */
  const handleQualityCheckPlanSearch = debounce((name) => {
    // 搜索质检方案
    getQualityCheckPlanList(1, 20, { name })
  }, 1000)

  /**
   * 选择质检方案
   * @param id 方案ID
   * @param options 方案详情
   */
  const handleQualityCheckPlanChange = (id, options) => {
    setQualityCheckPlanStatus(id ? '' : 'error')
    setSelectedPlan(options)
    if (!id) {
      setSelectedPlan(null)
      setQualityCheckRes(null)
      setRemark('')
      message.error('请选择质检方案')
      return
    }
    // 获取质检方案详情 - 返回的数据是完整的
    getQualityCheckPlanList(1, 1, { id })
  }

  /**
   * 提交、提交并进入下一条
   * @param type 提交类型 - submit 提交，submit-next 提交并进入下一条
   */
  const handleSubmit = debounce(async (type: 'submit' | 'submit-next' = 'submit') => {
    if (!qualityCheckRes?.id) {
      message.error('请选择质检方案')
      setQualityCheckPlanStatus('error')
      return
    } else {
      setQualityCheckPlanStatus('')
    }
    setLoading(true)
    const dimensionsScore = cloneDeep(qualityCheckRes.dimensionsScore)
    dimensionsScore?.forEach(item => {
      ['id', 'itemsTotalScoreFromAPI', 'itemsTotalScore'].forEach(key => {
        delete item[key]
      })
      item.items.forEach(subItem => {
        ['subId', 'subScore', 'requirement'].forEach(subKey => {
          delete subItem[subKey]
        })
      })
    })
    const params: AddQualityCheckResultParamsType = {
      qualityCheckPlanId: qualityCheckRes.id,
      score: currentTotalScore as number,
      callRecordId: Number(recordId),
      remark,
      dimensionsScore,
      callRecordType: pageType === PAGE_TYPE_ENUM.T3C ? CALL_RECORD_TYPE_ENUM.T3C : CALL_RECORD_TYPE_ENUM.MEETING,
    }
    const [, err] = await AddQualityCheckResultAPI(params)
    setLoading(false)
    if (err) {
      message.error(err.message)
      return
    }
    message.success('提交成功')
    if (type === 'submit-next') {
      handlePreviousOrNext('next')
    }
  }, 500)

  /** Tag click */
  const handleTagClick = (item) => {
    if (callRecordDetail?.callEventList?.length || pageType === PAGE_TYPE_ENUM.MEETING) {
      const time = pageType === PAGE_TYPE_ENUM.T3C ? item.diffTime : item.meetingAppearanceTime
      setSelectKeyWord(item)
      handleTimelineOnClick(time)
    }
  }

  return (
    <>
      <Card
        className="score-detail-page"
        title={
          <Link to={`/task/call-record?tabKey=${pageType}`}>
            <ArrowLeftOutlined style={{ marginRight: 10 }} />
            <span>
              {`${(callRecordDetail?.salesName && getStaffName(callRecordDetail?.salesName, callRecordDetail?.salesNameCh)) || '-'} 与 ${callRecordDetail?.customerName || '-'} 的通话`}
            </span>
          </Link>
        }
      >
        <Row>
          <Col span={11} className="left">
            <div className="top flex">
              <div className="quality-inspection-scheme">
                <p className="required-identification">选择质检方案</p>
                {authPoint?.[menuTaskCallScoreU] ? (
                  <Select
                    showSearch
                    allowClear
                    placeholder="请选择质检方案"
                    optionFilterProp="children"
                    onChange={handleQualityCheckPlanChange}
                    onSearch={handleQualityCheckPlanSearch}
                    value={selectedPlan?.id}
                    filterOption={false}
                    fieldNames={{ label: 'name', value: 'id' }}
                    options={qualityCheckList}
                    style={{ width: 300 }}
                    onBlur={debounce(() => getQualityCheckPlanList(1, 20), 500)}
                    status={qualityCheckPlanStatus}
                  />)
                  : <span>{qualityCheckList?.find(item => item.id === selectedPlan?.id)?.name || '-'}</span>}
              </div>
              <div className="total-score">
                <p>当前总分</p>
                <span style={{ fontSize: 24 }}>{currentTotalScore}</span>
              </div>
            </div>
            {!isEmpty(qualityCheckRes?.dimensionsScore) && (
              <div className="score-list">
                <p className="required-identification">评分项</p>
                <Collapse>
                  {qualityCheckRes?.dimensionsScore?.map((item, index) => (
                    <Collapse.Panel
                      header={
                        <>
                          <Typography.Text ellipsis={{ tooltip: item.name }}>{item.name}</Typography.Text>
                          <div className="score-container">{item.itemsTotalScore}/{item.itemsTotalScoreFromAPI}</div>
                        </>
                      }
                      key={item.id}
                    >
                      {item.items.map((subItem, subIndex) => (
                        <div className="score-item flex" key={subItem.subId}>
                          <Typography.Text ellipsis={{ tooltip: subItem.name }}>
                            {subItem.name}
                          </Typography.Text>
                          <Space size={authPoint?.[menuTaskCallScoreU] ? 4 : 0}>
                            {authPoint?.[menuTaskCallScoreU] ? (
                              <InputNumber
                                value={subItem.score}
                                onChange={val => handleScore(`dimensionsScore.${index}.items.${subIndex}.score`, val)}
                                precision={0}
                                min={0}
                                max={subItem.subScore}
                              />)
                              : <span>{subItem.score}</span>}
                            <span>/{subItem.subScore}</span>
                          </Space>
                        </div>
                      ))}
                    </Collapse.Panel>
                  ))}
                </Collapse>
                {authPoint?.[menuTaskCallScoreU] ? (
                  <Input.TextArea
                    showCount
                    value={remark}
                    onChange={e => setRemark(e.target.value)}
                    maxLength={500}
                    style={{ height: 120, marginTop: 40 }}
                    placeholder="请输入评语"
                  />)
                  : (
                    <>
                      <p style={{ marginTop: 26 }}>评语：</p>
                      <p style={{ textIndent: 28 }}>评语：{remark}</p>
                    </>)}
              </div>)}
          </Col>
          <Col span={11} offset={2} className="right">
            <p>通话录音</p>
            <div className="audio-container">
              {selectKeyWord && (
                // @ts-ignore
                <div
                  className="tag"
                  style={{ left: selectKeyWord.left, maxWidth: tabWidth }}
                >
                  {/* @ts-ignore */}
                  <Typography.Text ellipsis={{ tooltip: selectKeyWord.keyword }}>{selectKeyWord.keyword}</Typography.Text>
                </div>
              )}
              <audio style={{ display: open ? 'block' : 'none' }} ref={audioRef} preload="metadata" key={callRecordDetail?.recordId || uuid()} src={callRecordDetail?.recordUrl} controls />
              {!open && <Skeleton active title={false} paragraph={{ rows: 1, width: '100%' }} />}
            </div>
            {open ? (
              <>
                {Boolean(keyWordTag.length) && authPointMap.menuTaskCallScoreExt1.result && (
                  <div className="key-word-container">
                    <div className="header-container">
                      <p>匹配关键词</p>
                      <Radio.Group options={keyWordTypeOptions} size="middle" onChange={(e) => setKeyWordType(e.target.value)} value={keyWordType} optionType="button" />
                    </div>
                    <div className="tag-container">
                      {keyWordType === KeyWordTypeEnum.Time && (
                        <>
                          {keyWordTag.map((item, index) => (
                            <Tag
                              key={index}
                              color="default"
                              onClick={() => handleTagClick(item)}>
                              {/* @ts-ignore */}
                              <Typography.Text ellipsis={{ tooltip: item.keyword }}>{item.keyword}</Typography.Text>
                            </Tag>
                          ))}
                        </>
                      )}
                      {keyWordType === KeyWordTypeEnum.Group && (
                        <>
                          {['salesKeywordList', 'customerKeywordList'].map(key => (
                            <React.Fragment key={key}>
                              {!!callRecordDetail?.[key]?.length && (
                                <KeyWordTag
                                  type={key}
                                  list={callRecordDetail?.[key] as KeyWotdItemType[]}
                                  onClick={handleTagClick}
                                />
                              )}
                            </React.Fragment>
                          ))}
                        </>
                      )}
                    </div>
                  </div>
                )}
                <div className="call-timeline">
                  {PAGE_TYPE_MAP[pageType as PAGE_TYPE_ENUM] === TableIdEnum.TCCC_CALL_RECORD && Boolean(callRecordDetail?.callEventList?.length) && (
                    <div>
                      <p>通话时间线</p>
                      <Timeline mode="left">
                        {callRecordDetail?.callEventList?.map((item, index) => (
                          <Timeline.Item key={index} label={<Typography.Text type="secondary">{item.standardTime}</Typography.Text>}>
                            <Typography.Text ellipsis={{ tooltip: item.eventText }}>{item.eventText}</Typography.Text>
                            {/* <Button type="link" style={{ width: '100%' }} onClick={() => handleTimelineOnClick(item.diffTime)}>
                            </Button> */}
                          </Timeline.Item>
                        ))}
                      </Timeline>
                    </div>
                  )}
                  <div className="offline-speech-recognition">
                    {Boolean(callRecordDetail?.speechTextList?.length) && (
                      <>
                        {callRecordDetail?.callId && (
                          <>
                            <p>离线语音识别</p>
                            <p className="session-id-container">
                              <span className="id-label">Session ID：</span>
                              <Typography.Paragraph className="copy-text-container" copyable={{ text: callRecordDetail?.callId }}>
                                <Typography.Text type="secondary" className="session-id" ellipsis={{ tooltip: callRecordDetail?.callId }}>{callRecordDetail?.callId}</Typography.Text>
                              </Typography.Paragraph>
                            </p>
                          </>
                        )}
                        <div className="text-list" style={{ height: textListHeight }} ref={textListRef}>
                          {callRecordDetail?.speechTextList?.map((item, index) => (
                            <div
                              key={index}
                              className={`text-list-item flex ${item.isCustomerService ? 'customer-service' : 'client'} ${(audioCurrentTime >= item.startDiffSeconds && audioCurrentTime < item.endDiffSeconds) && 'active'}`}
                            >
                              {(!item.isCustomerService && callRecordDetail?.customerName) && (
                                <div className="name">
                                  <div>{callRecordDetail.customerName}</div>
                                  <div className="time">{item.startStandardTime}</div>
                                </div>
                              )}
                              {(item.isCustomerService && callRecordDetail?.salesName) && (
                                <div className="name">
                                  <div>{getStaffName(callRecordDetail?.salesName, callRecordDetail?.salesNameCh)}</div>
                                  <div className="time">{item.startStandardTime}</div>
                                </div>
                              )}
                              <div className="content">
                                {item.Message}
                              </div>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <>
                <Skeleton active className="key-word" paragraph={{ rows: 1, width: '100%' }} />
                <Skeleton active paragraph={{ rows: 1, width: '100%' }} />
                <Skeleton active paragraph={{ rows: 1, width: '100%' }} />
              </>
            )}
          </Col>
        </Row>
        <div className="btn-container">
          <Space size={12}>
            {/* 队列模式：显示评分进度 */}
            {isQueueMode && queuePosition && (
              <span style={{ marginRight: 8, color: '#666' }}>
                评分进度：{queuePosition.current}/{queuePosition.total}
              </span>
            )}
            {authPoint?.[menuTaskCallScoreU] && (
              <>
                {/* 队列模式：显示"提交并进入下一条" */}
                {isQueueMode && (
                  <Button loading={loading} disabled={loading || nextBtn} type="primary" onClick={() => handleSubmit('submit-next')}>提交并进入下一条</Button>
                )}
                <Button loading={loading} disabled={loading} type="default" onClick={() => handleSubmit('submit')}>提交</Button>
              </>
            )}
            {/* 队列模式：显示上一条/下一条导航按钮 */}
            {isQueueMode && (
              <>
                <Button loading={loading} disabled={loading || previousBtn} onClick={() => handlePreviousOrNext('previous')}>上一条</Button>
                <Button loading={loading} disabled={loading || nextBtn} onClick={() => handlePreviousOrNext('next')}>下一条</Button>
              </>
            )}
          </Space>
        </div>
      </Card>
    </>
  );
}

export default ScoreDetailPage
